import { useState } from 'react';
import type { ReactElement } from 'react';

import Snackbar from 'shared/ui/snackbar';
import type { SnackbarVariant } from 'shared/ui/snackbar';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import {
  useSetGlobalPromptSectionCategoriesMutation,
  useUpdateGlobalPromptSectionMutation,
  useUpdateProjectPromptSectionMutation,
} from '../api/promptSections.api';
import type { PromptSectionPermission } from '../api/promptSections.api';
import type { PromptSectionCategory } from '../lib/promptSectionCategories';
import PromptSectionCategoriesPicker from './PromptSectionCategoriesPicker';

const MAX_CATEGORIES = 4;

const PERMISSION_OPTIONS: { value: PromptSectionPermission; label: string }[] = [
  { value: 'hidden', label: 'Oculto' },
  { value: 'read-only', label: 'Solo lectura' },
  { value: 'suggest-edit', label: 'Sugerir edición' },
  { value: 'editable', label: 'Editable' },
];

export interface EditableSection {
  id: number;
  name: string;
  displayName: string;
  value: string;
  permission: PromptSectionPermission;
  categories?: PromptSectionCategory[];
}

interface Props {
  section: EditableSection;
  isGlobal: boolean;
  projectId: number;
  onClose: () => void;
}

const PromptSectionEditModal = ({ section, isGlobal, projectId, onClose }: Props): ReactElement => {
  const [displayName, setDisplayName] = useState(section.displayName);
  const [value, setValue] = useState(section.value);
  const [permission, setPermission] = useState<PromptSectionPermission>(section.permission);
  const [categories, setCategories] = useState<PromptSectionCategory[]>(section.categories ?? []);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; variant: SnackbarVariant }>({
    open: false,
    message: '',
    variant: 'success',
  });

  const [updateGlobal, { isLoading: isUpdatingGlobal }] = useUpdateGlobalPromptSectionMutation();
  const [updateProject, { isLoading: isUpdatingProject }] = useUpdateProjectPromptSectionMutation();
  const [setSectionCategories, { isLoading: isSavingCategories }] = useSetGlobalPromptSectionCategoriesMutation();

  const initialCategories = section.categories ?? [];
  const contentChanged =
    displayName !== section.displayName || value !== section.value || permission !== section.permission;
  const categoriesChanged =
    categories.length !== initialCategories.length ||
    categories.some((category) => !initialCategories.includes(category));

  const isSaving = isUpdatingGlobal || isUpdatingProject || isSavingCategories;
  const canSave =
    Boolean(displayName.trim() && value.trim()) &&
    (!isGlobal || categories.length > 0) &&
    (contentChanged || (isGlobal && categoriesChanged));

  const handleSave = async (): Promise<void> => {
    try {
      if (isGlobal) {
        if (contentChanged) {
          await updateGlobal({
            id: section.id,
            displayName,
            value,
            permission,
          }).unwrap();
        }
        if (categoriesChanged) {
          await setSectionCategories({ name: section.name, categories }).unwrap();
        }
      } else {
        await updateProject({
          id: section.id,
          projectId,
          name: section.name,
          displayName,
          value,
          permission,
        }).unwrap();
      }
      onClose();
    } catch {
      setSnackbar({ open: true, message: 'No se pudo guardar la sección.', variant: 'error' });
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Editar sección</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="prompt-section-name" className="text-sm font-medium">
              Identificador
            </Label>
            <Input id="prompt-section-name" value={section.name} disabled />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="prompt-section-display-name" className="text-sm font-medium">
              Descripción
            </Label>
            <Input
              id="prompt-section-display-name"
              value={displayName}
              disabled={isSaving}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="prompt-section-value" className="text-sm font-medium">
              Valor
            </Label>
            <Textarea
              id="prompt-section-value"
              className="field-sizing-fixed h-96 resize-none overflow-y-auto font-mono text-xs"
              value={value}
              disabled={isSaving}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>

          {isGlobal && (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Categorías</Label>
              <PromptSectionCategoriesPicker value={categories} onChange={setCategories} max={MAX_CATEGORIES} />
              <p className="text-xs text-text-muted">
                {categories.length === 0 ? 'Selecciona al menos una categoría' : `Máximo ${MAX_CATEGORIES}`}
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="prompt-section-permission" className="text-sm font-medium">
              Permiso
            </Label>
            <Select
              value={permission}
              disabled={isSaving}
              onValueChange={(v) => setPermission(v as PromptSectionPermission)}
            >
              <SelectTrigger id="prompt-section-permission" className="w-full">
                <SelectValue placeholder="Seleccionar permiso" />
              </SelectTrigger>
              <SelectContent>
                {PERMISSION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            size="sm"
            disabled={isSaving || !canSave}
            onClick={() => {
              void handleSave();
            }}
          >
            {isSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>

      <Snackbar
        open={snackbar.open}
        variant={snackbar.variant}
        message={snackbar.message}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Dialog>
  );
};

export default PromptSectionEditModal;
